import { useState } from 'react';
import { Link } from 'react-router-dom';
import toast from 'react-hot-toast';

import { useProject, useUpdateProject } from '../contexts/projects';
import { BOOTSTRAP_PHASE_LABEL, toPayload, validate, valuesFrom } from '../utils/form';
import ProjectAssignments from '../../project-teams/layouts/assignments';
import ProjectTeams from '../layouts/teams';
import ProjectFields from './fields';

import '../../clients/styles/form.css';

export default function EditProjectForm({ projectId }) {
    const { data, isLoading, error } = useProject(projectId);

    if (isLoading) return <p className="client-form__loading">Cargando proyecto...</p>;

    if (error) {
        return (
            <p className="client-form__failure">
                {error.message || 'No se pudo cargar el proyecto'}
            </p>
        );
    }

    // The detail answers the whole project — client, repositories, tiers,
    // teams, directives — and the shape it wraps the row in is not published.
    // Both readings are tried instead of assuming one.
    const project = data?.project ?? data;

    return <ProjectEditor key={project.id} project={project} detail={data} />;
}

function ProjectEditor({ project, detail }) {
    const updateProject = useUpdateProject(project.id);

    const [values, setValues] = useState(() => valuesFrom(project));
    const [errors, setErrors] = useState({});

    const change = (field, value) => {
        setValues(prev => ({ ...prev, [field]: value }));
        setErrors(prev => (prev[field] ? { ...prev, [field]: undefined } : prev));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        const found = validate(values);
        if (Object.keys(found).length) {
            setErrors(found);
            return;
        }

        try {
            await updateProject.mutateAsync(toPayload(values));
            toast.success('Proyecto guardado');
        } catch (err) {
            if (err.fields) setErrors(err.fields);
            else toast.error(err.message || 'No se pudo guardar el proyecto');
        }
    };

    const phase = BOOTSTRAP_PHASE_LABEL[project.bootstrap_phase] ?? project.bootstrap_phase;
    const repositories = detail?.repositories?.length;
    const tiers = detail?.tiers?.length;

    return (
        <section className="client-form">
            <header className="client-form__header">
                <h2 className="client-form__title">{project.name}</h2>
                <p className="client-form__subtitle">
                    Proyecto #{project.id} · <code>{project.slug}</code>
                    {phase && <> · Fase: {phase}</>}
                </p>
            </header>

            <form onSubmit={handleSubmit} noValidate>
                <div className="client-form__card">
                    <div className="client-form__section">
                        <h3 className="client-form__section-title">Datos del proyecto</h3>
                        <p className="client-form__section-text">
                            {/* The phase is not a form field: the API does not take it
                                on create and moving it is not a text box's job. */}
                            La fase la mueve el sistema, no este formulario.
                        </p>
                    </div>

                    <ProjectFields
                        clientLabel={detail?.client?.name}
                        values={values}
                        errors={errors}
                        onChange={change}
                        disabled={updateProject.isPending}
                    />
                </div>

                <div className="client-form__actions">
                    <Link to="/admin/projects" className="client-form__cancel">Volver</Link>
                    <button
                        type="submit"
                        className="client-form__submit"
                        disabled={updateProject.isPending}
                    >
                        {updateProject.isPending ? 'Guardando...' : 'Guardar cambios'}
                    </button>
                </div>
            </form>

            {/* Two panels answering two questions. The first: which teams reach
                this project and how far — that is the assignment and its scope.
                The second: who those people are, which the assignment does not
                say. */}
            <div className="client-form__card client-form__card--stacked">
                <ProjectAssignments projectId={project.id} />
            </div>

            <div className="client-form__card client-form__card--stacked">
                <ProjectTeams projectId={project.id} />
            </div>

            {(repositories != null || tiers != null) && (
                <p className="client-form__aside">
                    El detalle trae además {repositories ?? 0} repositorios y {tiers ?? 0} tiers.
                    Sus pantallas llegan con su tier.
                </p>
            )}
        </section>
    );
}
